import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { useGlobalStats } from '../hooks/useGlobalStats';

export function Home() {
  const { t } = useTranslation();
  const { stats, loading, error, refresh } = useGlobalStats();

  return (
    <div className="container-app py-12 sm:py-16 lg:py-20">
      {/* Hero section */}
      <div className="mx-auto max-w-3xl text-center">
        <h1 className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
          {t('site.title')}
        </h1>

        <p className="mb-2 text-lg text-accent sm:text-xl">
          {t('site.tagline')}
        </p>

        <p className="mb-8 text-gray-900 dark:text-gray-200 sm:text-lg">
          {t('site.description')}
        </p>

        <p className="mb-8 text-sm text-gray-900 dark:text-gray-200">
          {t('landing.no_login')}
        </p>

        <Link to="/vote">
          <Button size="lg" className="group">
            {t('landing.start_voting')}
            <ArrowRight
              className="h-5 w-5 transition-transform group-hover:translate-x-1"
              aria-hidden="true"
            />
          </Button>
        </Link>
      </div>

      {/* Global stats preview */}
      <div className="mx-auto mt-16 max-w-2xl">
        <Card variant="bordered">
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-lg font-semibold">
                <BarChart3 className="h-5 w-5 text-accent" aria-hidden="true" />
                {t('landing.global_stats')}
              </div>
              <button
                onClick={refresh}
                disabled={loading}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-800"
                aria-label="Refresh stats"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                />
              </button>
            </div>

            {error && (
              <p role="alert" className="mt-4 text-sm text-red-500">
                {t('common.error')}: {error}
              </p>
            )}

            {!error && stats && stats.total_votes > 0 && (
              <div className="mt-4 space-y-3" aria-live="polite">
                <div className="text-gray-600 dark:text-gray-200">
                  <p className="text-2xl font-bold text-black dark:text-white">
                    {stats.total_votes.toLocaleString()}
                  </p>
                  <p className="text-sm">
                    {t('landing.votes_cast', {
                      count: stats.total_votes,
                      images: stats.total_images,
                    })}
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-lg bg-ai-none/10 p-3 text-center">
                    <p className="text-xl font-bold text-ai-none">
                      {stats.like_rate_by_ai_status.none.toFixed(0)}%
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-200">
                      {t('landing.human_liked', {
                        percent: stats.like_rate_by_ai_status.none.toFixed(0),
                      })}
                    </p>
                  </div>
                  <div className="rounded-lg bg-ai-partial/10 p-3 text-center">
                    <p className="text-xl font-bold text-ai-partial">
                      {stats.like_rate_by_ai_status.partial.toFixed(0)}%
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-200">
                      AI-Assisted like rate
                    </p>
                  </div>
                  <div className="rounded-lg bg-ai-full/10 p-3 text-center">
                    <p className="text-xl font-bold text-ai-full">
                      {stats.like_rate_by_ai_status.full.toFixed(0)}%
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-200">
                      {t('landing.ai_liked', {
                        percent: stats.like_rate_by_ai_status.full.toFixed(0),
                      })}
                    </p>
                  </div>
                </div>

                {stats.most_liked_images.length > 0 && (
                  <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-600">
                    <p className="mb-2 text-sm font-medium">
                      {t('landing.most_liked')}
                    </p>
                    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-200">
                      {stats.most_liked_images.slice(0, 3).map((img, idx) => (
                        <li key={img.id} className="flex justify-between">
                          <span className="truncate">
                            {idx + 1}. {img.title_en}
                          </span>
                          <span className="ml-2 font-medium text-vote-yes">
                            {img.like_rate.toFixed(0)}%
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {!error && (!stats || stats.total_votes === 0) && !loading && (
              <div className="mt-4 text-center text-gray-600 dark:text-gray-200">
                <p className="text-sm">
                  {t('landing.no_votes_yet')}
                </p>
                <p className="mt-2 text-xs text-gray-400">
                  Be the first to vote!
                </p>
              </div>
            )}

            {loading && !stats && (
              <div className="mt-4 flex items-center justify-center" role="status" aria-busy="true">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" aria-hidden="true" />
                <span className="sr-only">{t('common.loading')}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
